"use client"

// Shared types, formatters, and detail blocks for the Opportunities feed - used by
// the cards, the table, and the drawer so there is ONE source for each fact.

import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type MouseEvent as ReactMouseEvent } from "react"
import { createPortal } from "react-dom"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { Button, Badge, Tooltip } from "@signalmax/ui"
import { useCareerApi } from "@/components/max/useCareerApi"
import { trackCareer } from "@/lib/max/analytics"
import { bandFor, isCardReasonTruthful, isEligibleStatus, type Band } from "@/lib/career/bands"

// ── response shapes (mirror /api/career/evaluations) ──
export interface Driver { key: string; label: string; weight: number; points: number; hasData: boolean; reason: string }
export interface Evaluation {
  opportunity_id: string
  eligibility: { status: string; reason: string; evidence: string[]; confidence: number; missing: string[] }
  fit: { score: number | null; confidence: number; coverage: number; priority: string; drivers: Driver[]; strengths: string[]; gaps: string[] }
}
export interface FeedOpp {
  id: string; company_id: string | null; role_title: string; company_name: string | null; logo_url: string | null; employer_website: string | null; employer_industry: string | null; is_government?: boolean; career_track: string | null; subtype: string | null
  location: string | null; location_count?: number | null; work_mode: string | null; term_season: string | null; term_year: number | null; term_inferred?: boolean
  application_deadline: string | null; apply_url: string | null; primary_source_url: string | null
  start_date: string | null; duration_weeks: number | null; duration_text: string | null
  compensation: string | null; comp_min: number | null; comp_max: number | null; comp_period: string | null
  comp_currency: string | null; comp_min_monthly: number | null
  class_year_min: number | null; class_year_max: number | null // eligible graduation-year window (nulls = open-ended)
}
export type ApplicationStatus = "saved" | "applied" | "interviewing" | "offer" | "closed" | "tabled"
export interface FeedItem { opportunity: FeedOpp; evaluation: Evaluation; status?: ApplicationStatus | null; aligned_to_goal?: boolean; less_like_this?: boolean; track_relation?: "core" | "adjacent" }
export interface LockedPreview { role_title: string; company_name: string | null; career_track: string | null; eligibility_status: string }
export interface FeedResp {
  items: FeedItem[]
  counts: { total: number; eligible: number; unclear: number; persisted: number; new_today: number; new_this_week: number; new_this_month: number }
  pipeline?: { saved: number; applied: number; interviewing: number; offer: number; closed: number; tabled: number }
  access: { tier: string; match_cap: number | null; locked_count: number; beta_ungated: boolean }
  locked_preview: LockedPreview[]
}

// The application-pipeline stages, in order. "none" (absence) is the unset state, shown as the
// "＋ Save" affordance. STATUS_META drives the label + tone everywhere the stage is displayed
// (card, table, drawer, /applications lanes) - one source, no drift.
// Order = the active funnel, then "Next year" (tabled) as a trailing PARKED bucket. "tabled" is not
// a funnel stage; it's for a role you can't pursue this cycle but want to revisit — see repository.ts.
export const STATUS_STAGES: ApplicationStatus[] = ["saved", "applied", "interviewing", "offer", "closed", "tabled"]
export const STATUS_META: Record<ApplicationStatus, { label: string; tone: "info" | "success" | "warning" | "neutral"; hint: string }> = {
  saved: { label: "Saved", tone: "neutral", hint: "On your list to apply to" },
  applied: { label: "Applied", tone: "info", hint: "You've submitted an application" },
  interviewing: { label: "Interviewing", tone: "warning", hint: "You're in the interview process" },
  offer: { label: "Offer", tone: "success", hint: "You received an offer" },
  closed: { label: "Closed", tone: "neutral", hint: "This application is over — rejected, withdrawn, or declined. Kept for your records; the role stays in your feed." },
  tabled: { label: "Next year", tone: "info", hint: "Parked for a future cycle — you can't apply yet (e.g. a class-year requirement) but want to revisit next year. Doesn't hide the role." },
}
const statusToneColor = (tone: "info" | "success" | "warning" | "neutral"): string =>
  tone === "success" ? "var(--status-success-text)" : tone === "info" ? "var(--status-info-text)" : tone === "warning" ? "var(--status-warning-text)" : "var(--text-muted)"

// Position a fixed popover off its trigger. Opens BELOW by default, but flips ABOVE when the
// trigger sits low in the viewport — otherwise the menu's items render below the fold and only its
// header shows (the card/drawer footer is near the bottom). Clamps to the viewport and caps the
// height so the whole menu is always reachable (scrolls if it can't fit either way).
type MenuPos = { left: number; maxHeight: number } & ({ top: number; bottom?: undefined } | { bottom: number; top?: undefined })
function anchoredMenuPos(r: DOMRect, preferredLeft: number, menuW: number): MenuPos {
  const gap = 6, margin = 8
  const roomBelow = window.innerHeight - r.bottom - margin
  const roomAbove = r.top - margin
  const left = Math.max(margin, Math.min(preferredLeft, window.innerWidth - menuW - margin))
  if (roomBelow < 200 && roomAbove > roomBelow) return { bottom: window.innerHeight - r.top + gap, left, maxHeight: Math.max(120, roomAbove - gap) }
  return { top: r.bottom + gap, left, maxHeight: Math.max(120, roomBelow - gap) }
}

// The application-stage control on a role card / row / drawer. Unset shows "＋ Save"; set shows the
// stage label in its tone. Click opens a menu to set ANY stage directly (Saved · Applied ·
// Interviewing · Offer · Closed) or remove it — the same field /applications and Overview's pipeline
// read. Direct-set, not a cycle, so every stage is one click and discoverable. The menu is
// position:fixed off the trigger so an overflow:auto card/drawer never clips it; always
// stopPropagation so it never opens the drawer. `compact` for the table row.
export function StatusMenu({ status, onSet, compact }: { status: ApplicationStatus | null | undefined; onSet: (s: ApplicationStatus | "none") => void; compact?: boolean }) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<MenuPos | null>(null)
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const btnRef = useRef<HTMLButtonElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const MENU_W = 210
  const meta = status ? STATUS_META[status] : null
  const toneColor = statusToneColor(meta?.tone ?? "neutral")
  const set = Boolean(status)
  const openMenu = () => {
    const r = btnRef.current?.getBoundingClientRect()
    if (r) setPos(anchoredMenuPos(r, r.left, MENU_W))
    setOpen(true)
  }
  useEffect(() => {
    if (!open) return
    // The menu is PORTALED to <body> (see below), so an outside-click check must accept clicks on
    // the trigger AND on the portaled menu.
    const onDoc = (e: MouseEvent) => { const t = e.target as Node; if (wrapRef.current?.contains(t) || menuRef.current?.contains(t)) return; setOpen(false) }
    const onScroll = () => setOpen(false)
    document.addEventListener("mousedown", onDoc)
    window.addEventListener("scroll", onScroll, true)
    return () => { document.removeEventListener("mousedown", onDoc); window.removeEventListener("scroll", onScroll, true) }
  }, [open])
  const choose = (s: ApplicationStatus | "none") => (e: ReactMouseEvent) => { e.stopPropagation(); setOpen(false); onSet(s) }
  const itemStyle: CSSProperties = { display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left", background: "transparent", border: "none", cursor: "pointer", fontSize: 12.5, padding: "7px 12px", whiteSpace: "nowrap" }
  return (
    <div ref={wrapRef} style={{ position: "relative", display: "inline-flex" }} onClick={(e) => e.stopPropagation()}>
      <button
        ref={btnRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); open ? setOpen(false) : openMenu() }}
        title="Set application status"
        aria-haspopup="menu"
        aria-expanded={open}
        style={{
          display: "inline-flex", alignItems: "center", gap: 4, whiteSpace: "nowrap", cursor: "pointer",
          fontSize: compact ? 11.5 : 12, fontWeight: 600, minWidth: compact ? 88 : undefined, justifyContent: "center",
          padding: compact ? "3px 8px" : "4px 10px", borderRadius: 999,
          color: set ? toneColor : "var(--text-muted)",
          background: set ? `color-mix(in srgb, ${toneColor} 14%, transparent)` : "transparent",
          border: `1px solid ${set ? `color-mix(in srgb, ${toneColor} 40%, transparent)` : "var(--border-subtle)"}`,
        }}
      >
        {set ? meta!.label : "＋ Save"}
        <span aria-hidden style={{ fontSize: 9, opacity: 0.7 }}>▾</span>
      </button>
      {open && pos && typeof document !== "undefined" && createPortal(
        // theme-max/smx: the portal target is <body>, OUTSIDE the app's themed tree, so the CSS
        // variables (--surface, --border-subtle, …) only resolve if we re-apply the theme class here —
        // otherwise the menu renders transparent and overlaps the card. Mirrors GlobalSearch's overlay.
        <div ref={menuRef} className="theme-max smx" role="menu" onClick={(e) => e.stopPropagation()} style={{ position: "fixed", top: pos.top, bottom: pos.bottom, left: pos.left, zIndex: 1000, width: MENU_W, maxHeight: pos.maxHeight, overflowY: "auto", background: "var(--surface)", border: "1px solid var(--border-subtle)", borderRadius: 10, boxShadow: "0 8px 28px rgba(0,0,0,0.18)", padding: "4px 0" }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-muted)", padding: "6px 12px 4px" }}>Application status</div>
          {STATUS_STAGES.map((s) => {
            const m = STATUS_META[s]
            const active = status === s
            return (
              <button key={s} role="menuitemradio" aria-checked={active} title={m.hint} style={{ ...itemStyle, fontWeight: active ? 700 : 600, color: active ? "var(--text-primary)" : "var(--text-secondary)" }} onClick={choose(s)}>
                <span aria-hidden style={{ width: 8, height: 8, borderRadius: 999, background: statusToneColor(m.tone), flexShrink: 0 }} />
                <span style={{ flex: 1 }}>{m.label}</span>
                {active && <span aria-hidden style={{ color: "var(--accent-text)" }}>✓</span>}
              </button>
            )
          })}
          {set && (
            <>
              <div style={{ height: 1, background: "var(--border-subtle)", margin: "4px 0" }} />
              <button role="menuitem" style={{ ...itemStyle, color: "var(--text-muted)" }} onClick={choose("none")}>Remove from my list</button>
            </>
          )}
        </div>,
        document.body,
      )}
    </div>
  )
}
export interface Tailoring {
  match_score: number | null; matched_keywords: string[]; missing_keywords: string[]
  summary: string; emphasize: string[]; rephrase: { from: string; to: string }[]
  address_gaps: string[]; bullets: string[]; available: boolean
}

// ── company header (mirrors /api/career/opportunities/:id/detail) ──
export interface CompanyHeader {
  name: string | null; website: string | null; linkedin_url: string | null
  industry: string | null; size: string | null; hq: string | null; logo_url: string | null; source: string
}
export interface OpportunityDetail {
  id: string; role_title: string; description: string | null; work_authorization: string | null; degree_requirement: string | null
  recruiter_name: string | null // named recruiter/hiring contact the posting lists, if any
  skills: { required: string[]; preferred: string[]; coursework: string[] }
  read_more_url: string | null; company: CompanyHeader
}

export const TRACKS = [
  { key: "", label: "All" },
  { key: "ai_ml", label: "AI / ML" },
  { key: "software", label: "Software" },
  { key: "data", label: "Data" },
  { key: "quant", label: "Quant" },
  { key: "fde", label: "Forward Deployed" },
  { key: "product", label: "Product" },
  { key: "design", label: "Design" },
  { key: "hardware", label: "Hardware" },
  { key: "security", label: "Security" },
  { key: "investing", label: "Investing" },
  { key: "finance", label: "Finance" },
  { key: "consulting", label: "Consulting" },
  { key: "marketing", label: "Marketing" },
  { key: "operations", label: "Operations" },
  { key: "life_sciences", label: "Life Sciences" },
  { key: "engineering", label: "Engineering" },
  { key: "healthcare", label: "Healthcare" },
  { key: "education", label: "Education" },
  { key: "sales", label: "Sales & BD" },
  { key: "accounting", label: "Accounting" },
  { key: "legal", label: "Legal" },
  { key: "research", label: "Research" },
  { key: "supply_chain", label: "Supply Chain" },
  { key: "communications", label: "Communications" },
  { key: "human_resources", label: "Human Resources" },
  { key: "policy", label: "Policy & Government" },
  { key: "sustainability", label: "Sustainability & Energy" },
  { key: "hospitality", label: "Hospitality & Tourism" },
  { key: "other", label: "Other" },
]

export const PRIORITY_META: Record<string, { label: string; fg: string; bg: string; bd: string }> = {
  APPLY_NOW: { label: "Apply now", fg: "var(--accent-text)", bg: "rgba(241,48,9,0.12)", bd: "rgba(241,48,9,0.35)" },
  HIGH: { label: "Strong fit", fg: "var(--status-success-text)", bg: "rgba(61,190,122,0.14)", bd: "var(--status-success-border)" },
  GOOD: { label: "Good fit", fg: "var(--status-info-text)", bg: "rgba(120,160,220,0.14)", bd: "var(--status-info-border)" },
  REVIEW: { label: "Worth a look", fg: "var(--status-warning-text)", bg: "rgba(245,165,95,0.14)", bd: "var(--status-warning-border)" },
  LOW: { label: "Low fit", fg: "var(--text-muted)", bg: "var(--surface-sunken, transparent)", bd: "var(--border-subtle)" },
  INELIGIBLE: { label: "Not eligible", fg: "var(--text-muted)", bg: "var(--surface-sunken, transparent)", bd: "var(--border-subtle)" },
}

// "Toward your goal" marker: shown on roles that advance the student's stated NEXT
// career goal. A gentle, honest signal (the feed's order is still eligibility -> fit);
// it just makes the goal-aligned matches legible.
export function GoalChip({ compact }: { compact?: boolean }) {
  return (
    <span
      title="Advances the next career goal you set"
      style={{ display: "inline-flex", alignItems: "center", gap: 4, whiteSpace: "nowrap", fontSize: compact ? 11 : 11.5, fontWeight: 600, padding: compact ? "2px 8px" : "3px 9px", borderRadius: 999, color: "var(--accent-text)", background: "rgba(241,48,9,0.10)", border: "1px solid rgba(241,48,9,0.30)" }}
    >
      ↗ Toward your goal
    </span>
  )
}

// ── "Not for me" (hide) ───────────────────────────────────────────────────────
export type HideScope = "opportunity" | "company" | "title_pattern"
export interface HidePayload { opportunity_id?: string; company_id?: string; title_pattern?: string; reason?: string }

// The "why not for me" chips, shared by the For You queue AND the feed card's "Not for me" menu so
// both capture the SAME reason (values MUST be in HIDE_REASONS / career_feed_hides_reason_chk). One
// list, used in both places - the reason feeds the relevance loop no matter where the student hides.
export const HIDE_REASON_CHIPS: { value: string; label: string }[] = [
  { value: "not_relevant", label: "Not relevant" },
  { value: "wrong_role", label: "Wrong role" },
  { value: "wrong_location", label: "Wrong location" },
  { value: "wrong_timing", label: "Wrong timing" },
  { value: "not_qualified", label: "Not qualified" },
  { value: "already_applied", label: "Already applied" },
]

// Derive a short, meaningful title phrase to hide "roles like this" - strip the
// intern/term/year/mode noise and keep the first few real words, so the pattern reads
// like the kind of role (e.g. "Commercial Construction Intern - Summer 2027" -> "commercial
// construction") rather than the whole specific title. Mirrors normalizeTitlePattern.
const TITLE_NOISE = /\b(intern(ship)?|co-?op|summer|fall|spring|winter|20\d{2}|program|student|undergraduate|graduate|paid|remote|hybrid|onsite|full[- ]?time|part[- ]?time)\b/gi
export function titlePatternFrom(title: string | null): string {
  const cleaned = String(title || "").toLowerCase().replace(TITLE_NOISE, " ").replace(/[^a-z0-9 ]+/g, " ").replace(/\s+/g, " ").trim()
  const words = cleaned.split(" ").filter((w) => w.length > 2)
  return (words.slice(0, 3).join(" ") || cleaned.slice(0, 40)).trim()
}

// Marks a role surfaced from a RELATED track to backfill a thin filtered feed. Honest:
// it names the related field (e.g. "Related: Research") so a student never mistakes an
// adjacent role for an exact-track match. Only shown when the feed is filtered to one track.
export function AdjacentChip({ track }: { track: string | null }) {
  return (
    <span
      title="From a related field - shown to round out a track that has few internships of its own."
      style={{ display: "inline-flex", alignItems: "center", gap: 4, whiteSpace: "nowrap", fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 999, color: "var(--status-info-text)", background: "color-mix(in srgb, var(--status-info-text) 12%, transparent)", border: "1px solid var(--status-info-border)" }}
    >
      ↔ Related: {trackLabel(track)}
    </span>
  )
}

// A subtle marker on roles demoted because the student hid a similar one. Honest and
// legible: the feed still SHOWS them (lower), it does not hide them.
export function LessLikeThisChip() {
  return (
    <span
      title="Ranked lower because you hid a similar role. Manage what you have hidden from “Hidden”."
      style={{ display: "inline-flex", alignItems: "center", gap: 4, whiteSpace: "nowrap", fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 999, color: "var(--text-muted)", background: "var(--surface-sunken, transparent)", border: "1px solid var(--border-subtle)" }}
    >
      ↓ Less like this
    </span>
  )
}

// The "Not for me" control: a ghost button opening a small popover. Step 1 picks the hide SCOPE
// (this role / all from company / roles like this); "Not for me" (role scope) goes to step 2 to pick
// an optional reason, which is persisted with the hide (same signal the For You queue captures).
// Calls onHide(scope, payload, label); the parent persists + updates. Always stopPropagation so it
// never opens the drawer. `compact` for the table row.
export function NotInterestedMenu({ opp, onHide, compact }: { opp: FeedOpp; onHide: (scope: HideScope, payload: HidePayload, label: string) => void; compact?: boolean }) {
  const [open, setOpen] = useState(false)
  const [reasonStep, setReasonStep] = useState(false) // after "Not for me": pick WHY (opportunity scope only)
  const [pos, setPos] = useState<MenuPos | null>(null)
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const btnRef = useRef<HTMLButtonElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const MENU_W = 240
  // The menu is PORTALED to <body> so a transformed/overflow ancestor (the card's hover-lift
  // transform, the drawer's overflow) can never reposition or clip it — position:fixed then resolves
  // against the viewport. anchoredMenuPos flips it above the trigger when the footer sits low, clamps
  // to the viewport, and caps the height. Recompute-free: close on any scroll or outside click.
  const openMenu = () => {
    const r = btnRef.current?.getBoundingClientRect()
    if (r) setPos(anchoredMenuPos(r, r.right - MENU_W, MENU_W))
    setOpen(true)
  }
  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => { const t = e.target as Node; if (wrapRef.current?.contains(t) || menuRef.current?.contains(t)) return; setOpen(false) }
    const onScroll = () => setOpen(false)
    document.addEventListener("mousedown", onDoc)
    window.addEventListener("scroll", onScroll, true)
    return () => { document.removeEventListener("mousedown", onDoc); window.removeEventListener("scroll", onScroll, true) }
  }, [open])
  useEffect(() => { if (!open) setReasonStep(false) }, [open]) // reset to step 1 whenever the menu closes
  const pattern = titlePatternFrom(opp.role_title)
  const choose = (scope: HideScope, payload: HidePayload, label: string) => (e: ReactMouseEvent) => {
    e.stopPropagation(); setOpen(false); onHide(scope, payload, label)
  }
  const itemStyle: CSSProperties = { display: "block", width: "100%", textAlign: "left", background: "transparent", border: "none", cursor: "pointer", fontSize: 12.5, color: "var(--text-primary)", padding: "8px 12px", whiteSpace: "nowrap" }
  const hdrStyle: CSSProperties = { fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-muted)", padding: "6px 12px 4px" }
  return (
    <div ref={wrapRef} style={{ position: "relative", display: "inline-flex" }} onClick={(e) => e.stopPropagation()}>
      <button
        ref={btnRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); open ? setOpen(false) : openMenu() }}
        title="Not for me - hide this from your feed"
        aria-label="Not for me"
        style={{ display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer", fontSize: compact ? 11.5 : 12, fontWeight: 600, padding: compact ? "3px 8px" : "4px 10px", borderRadius: 999, color: "var(--text-muted)", background: "transparent", border: "1px solid var(--border-subtle)" }}
      >
        {compact ? "⊘" : "⊘ Not for me"}
      </button>
      {open && pos && typeof document !== "undefined" && createPortal(
        // theme-max/smx: re-apply the theme class at the <body> portal root so the CSS variables
        // resolve (see StatusMenu note) — else the menu renders transparent over the card.
        <div
          ref={menuRef}
          className="theme-max smx"
          role="menu"
          onClick={(e) => e.stopPropagation()}
          style={{ position: "fixed", top: pos.top, bottom: pos.bottom, left: pos.left, zIndex: 1000, width: MENU_W, maxHeight: pos.maxHeight, overflowY: "auto", background: "var(--surface)", border: "1px solid var(--border-subtle)", borderRadius: 10, boxShadow: "0 8px 28px rgba(0,0,0,0.18)", padding: "4px 0" }}
        >
          {!reasonStep ? (
            <>
              <div style={hdrStyle}>Hide from my feed</div>
              {/* Step 1: pick the SCOPE. "Not for me" (this role) goes to the reason step so the hide
                  carries a why - the same signal the For You queue captures. */}
              <button role="menuitem" style={itemStyle} onClick={(e) => { e.stopPropagation(); setReasonStep(true) }}>Not for me</button>
              {opp.company_id && opp.company_name && (
                <button role="menuitem" style={itemStyle} onClick={choose("company", { company_id: opp.company_id }, `all roles from ${opp.company_name}`)}>Hide all from {opp.company_name}</button>
              )}
              {pattern && (
                <button role="menuitem" style={itemStyle} onClick={choose("title_pattern", { title_pattern: pattern }, `roles like “${pattern}”`)}>Hide roles like “{pattern}”</button>
              )}
            </>
          ) : (
            <>
              {/* Step 2: WHY (optional). Each reason hides this role AND records the reason; "Just hide
                  it" still hides with no reason. Mirrors the For You "Not for me" chips. */}
              <div style={hdrStyle}>Why not for you?</div>
              {HIDE_REASON_CHIPS.map((r) => (
                <button key={r.value} role="menuitem" style={itemStyle} onClick={choose("opportunity", { opportunity_id: opp.id, reason: r.value }, "this role")}>{r.label}</button>
              ))}
              <button role="menuitem" style={{ ...itemStyle, color: "var(--text-muted)" }} onClick={choose("opportunity", { opportunity_id: opp.id }, "this role")}>Just hide it</button>
            </>
          )}
        </div>,
        document.body,
      )}
    </div>
  )
}

export function eligibilityChip(status: string): { tone: "success" | "warning" | "neutral"; label: string } {
  switch (status) {
    case "CONFIRMED_ELIGIBLE": return { tone: "success", label: "Eligible" }
    case "LIKELY_ELIGIBLE": return { tone: "success", label: "Likely eligible" }
    case "UNCERTAIN": return { tone: "warning", label: "Check eligibility" }
    case "LIKELY_INELIGIBLE": return { tone: "warning", label: "Likely ineligible" }
    case "CONFIRMED_INELIGIBLE": return { tone: "neutral", label: "Not eligible" }
    default: return { tone: "neutral", label: status }
  }
}
// Plain-language explanation for the eligibility chip, so "Check eligibility" isn't a dead end. The
// UNCERTAIN case names the usual cause (missing graduation year) and what to do about it.
export function eligibilityTip(status: string): { title: string; body: string } {
  switch (status) {
    case "CONFIRMED_ELIGIBLE": return { title: "Eligible", body: "Your class year and the posting's stated requirements line up, so you qualify to apply." }
    case "LIKELY_ELIGIBLE": return { title: "Likely eligible", body: "You probably qualify; the posting didn't state every requirement, so double-check the details before applying." }
    case "UNCERTAIN": return { title: "Check eligibility", body: "We couldn't confirm you qualify, usually because your graduation year is missing. Add it in Me and we'll check." }
    case "LIKELY_INELIGIBLE": return { title: "Likely ineligible", body: "The posting's class-year or other requirements probably rule you out, but the listing wasn't explicit." }
    case "CONFIRMED_INELIGIBLE": return { title: "Not eligible", body: "The posting's requirements (e.g. class year) rule you out for this cycle." }
    default: return { title: "Eligibility", body: "Whether you qualify to apply, based on your class year and the posting's stated requirements." }
  }
}
// One definition, in web/lib/career/bands.ts (pure), re-exported here for the many callers that
// already import it from feedShared.
export const isEligible = isEligibleStatus
export const trackLabel = (t: string | null) => TRACKS.find((x) => x.key === t)?.label ?? (t || "-")
// The role's SEASON/term for the card badge + drawer subtitle: "Summer 2027", "Summer 2027 (est.)"
// when inferred from the posting date, and "Term TBD" when there's no season at all. Every role
// carries a timing badge now (founder policy 2026-09-21: uncertain-when roles are shown + labeled +
// score-penalized, not hidden). A separate deadline still shows in the card footer / drawer detail.
export const seasonLabel = (o: FeedOpp) => {
  const base = [o.term_season && o.term_season[0].toUpperCase() + o.term_season.slice(1), o.term_year].filter(Boolean).join(" ")
  if (!base) return "Term TBD"
  return o.term_inferred ? `${base} (est.)` : base
}
// The role's actionable "when" for a single compact slot with no separate deadline nearby (the feed
// table, onboarding, the teaser): a stated term ("Summer 2027"), an inferred one ("Summer 2027
// (est.)"), else the real deadline ("Apply by <date>"), else "Term TBD".
export const termLabel = (o: FeedOpp) => {
  const base = [o.term_season && o.term_season[0].toUpperCase() + o.term_season.slice(1), o.term_year].filter(Boolean).join(" ")
  if (base) return o.term_inferred ? `${base} (est.)` : base
  const dl = fmtDate(o.application_deadline)
  if (dl) return `Apply by ${dl}`
  return "Term TBD"
}

export function monogram(name: string | null): string {
  const words = String(name || "").trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return "-"
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
  return (words[0][0] + words[1][0]).toUpperCase()
}

const PERIOD_ABBR: Record<string, string> = { hourly: "/hr", weekly: "/wk", biweekly: "/2wk", monthly: "/mo", yearly: "/yr", stipend: " stipend", other: "" }
const fmtNum = (n: number | null) => (n == null ? "" : n >= 1000 ? n.toLocaleString() : String(n))
export function wageLabel(o: FeedOpp): string | null {
  // Guard a sentinel/absurd comp (e.g. "$0.00 - $10,000,000.00" from a source that emits a
  // placeholder ceiling): a 7-figure internship "range" is not real pay, so show nothing
  // rather than a joke figure. Applies even when a pre-formatted `compensation` string exists.
  if (o.comp_max != null && o.comp_max >= 1_000_000) return null
  if (o.compensation) return o.compensation
  if (o.comp_min == null && o.comp_max == null) return null
  const cur = !o.comp_currency || o.comp_currency === "USD" ? "$" : `${o.comp_currency} `
  const range = o.comp_max != null && o.comp_max !== o.comp_min ? `${fmtNum(o.comp_min)}-${fmtNum(o.comp_max)}` : fmtNum(o.comp_min ?? o.comp_max)
  return `${cur}${range}${o.comp_period ? (PERIOD_ABBR[o.comp_period] ?? "") : ""}`
}
export function fmtDate(d: string | null): string | null {
  if (!d) return null
  try { return new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) } catch { return d }
}
export function durationLabel(o: FeedOpp): string | null {
  if (o.duration_text) return o.duration_text
  if (o.duration_weeks) return `${o.duration_weeks} wks`
  return null
}
// Honest location label: lead with the WORK MODE when it is the decision-relevant fact, so a REMOTE
// role never reads as an on-site one. ~1,449 remote roles carry a physical-city `location` (the HQ the
// posting names) with no "remote" in the text, so a bare `location` made them look on-site - which is
// exactly why a Remote filter looked like it "leaked" a Boston role: the role IS remote (matched on
// work_mode=remote), the LABEL lied. Remote/hybrid lead with the mode and keep the city as secondary
// context; on-site shows the city. Used by the card, the table, and the drawer header in lockstep.
export function locationLabel(o: { location?: string | null; work_mode?: string | null; location_count?: number | null }): string {
  const loc = (o.location || "").trim()
  const wm = (o.work_mode || "").toLowerCase()
  let base: string
  if (wm === "remote") base = !loc || /remote/i.test(loc) ? "Remote" : `Remote · ${loc}`
  else if (wm === "hybrid") base = !loc || /hybrid/i.test(loc) ? "Hybrid" : `Hybrid · ${loc}`
  else if (wm === "onsite") base = loc || "On-site"
  else base = loc || "-"
  // One posting syndicated to many cities (Adzuna fans a remote/national req out per metro): the feed
  // shows ONE representative row and discloses the rest here instead of hiding them. location_count is
  // the size of that (employer, role, term) group - see career_mark_feed_location_dupes().
  const n = o.location_count ?? 0
  if (n > 1 && base !== "-") base += ` +${n - 1} locations`
  return base
}

export const linkBtn: CSSProperties = { background: "transparent", border: "none", color: "var(--accent-text)", cursor: "pointer", fontSize: 12.5, fontWeight: 600, padding: 0 }

// A stored logo_url we must NOT trust: builtin.com serves generic placeholder assets
// (company-logo-fallback, fallback-image-NN, "corporate avatar", screenshots, "image (N)")
// that were scraped onto the wrong companies. They return HTTP 200, so the onError chain
// below can't catch them — a valid-but-wrong image would win. Skipping them lets the
// domain-derived logo (or a clean monogram) take over. Scoped to builtin.com so a legit
// logo elsewhere whose filename happens to contain "image" is never dropped. (Wrong-company
// VALID logos — e.g. Salesforce carrying nanostuffs' real logo — can't be detected here;
// those are nulled at the data layer by scripts/career-fix-contaminated-logos.mjs.)
function isJunkBuiltinLogo(url: string): boolean {
  if (!/(^|\.)builtin\.com\//i.test(url)) return false
  const file = decodeURIComponent(url.split("/").pop() || "").toLowerCase()
  return /company-logo-fallback|fallback-image|corporate[ _-]?avatar|screen[ _-]?shot|^image[ _(]/.test(file)
}

// Build ordered logo candidates for a company: the stored logo_url first, then, from
// the employer's own domain, the same multi-source chain web/components/app/CompanyLogo
// uses (Clearbit -> Google favicon -> DuckDuckGo -> logo.dev). content_companies.logo_url
// is often a signed, EXPIRING LinkedIn CDN URL that 404s, so a single source leaves too
// many blanks; we walk the list on each error and only fall to the monogram once every
// source has failed.
function logoCandidates(logoUrl?: string | null, website?: string | null): string[] {
  const out: string[] = []
  if (logoUrl && !isJunkBuiltinLogo(logoUrl)) out.push(logoUrl)
  if (website) {
    try {
      const u = website.startsWith("http") ? website : `https://${website}`
      const domain = new URL(u).hostname.replace(/^www\./, "")
      if (domain && domain.includes(".")) {
        out.push(`https://logo.clearbit.com/${domain}`)
        out.push(`https://www.google.com/s2/favicons?domain=${domain}&sz=128`)
        out.push(`https://icons.duckduckgo.com/ip3/${domain}.ico`)
        out.push(`https://img.logo.dev/${domain}?token=pk_X-1ZO13GSgeOoUrIuJ6GMQ`)
      }
    } catch { /* unparseable website - stored logo (if any) is all we have */ }
  }
  return out
}

export function MonogramTile({ name, logoUrl, website, size = 40 }: { name: string | null; logoUrl?: string | null; website?: string | null; size?: number }) {
  const candidates = useMemo(() => logoCandidates(logoUrl, website), [logoUrl, website])
  // Index into `candidates`; advance past each source that fails to load. When it runs
  // off the end, show the monogram instead of a broken image.
  const [idx, setIdx] = useState(0)
  useEffect(() => { setIdx(0) }, [logoUrl, website])
  const src = candidates[idx]
  if (src) {
    // eslint-disable-next-line @next/next/no-img-element
    return <img src={src} alt="" width={size} height={size} onError={() => setIdx((i) => i + 1)} style={{ width: size, height: size, borderRadius: 10, objectFit: "contain", background: "var(--surface)", border: "1px solid var(--border-subtle)", flexShrink: 0 }} />
  }
  return (
    <div aria-hidden style={{
      width: size, height: size, flexShrink: 0, borderRadius: 10, display: "grid", placeItems: "center",
      background: "var(--surface-raised, var(--surface-sunken, var(--surface)))", border: "1px solid var(--border-subtle)",
      fontWeight: 700, fontSize: size * 0.32, color: "var(--text-primary)", letterSpacing: "-0.01em",
      fontFamily: "var(--font-display, inherit)",
    }}>{monogram(name)}</div>
  )
}

// ── résumé tailoring, shared by the card action, the drawer section, and the
// résumé screen's "Against a role" review ──
// `resumeId` (optional) tailors a SPECIFIC résumé version; omitted, the route uses the
// candidate's primary (the drawer/card behaviour). Callers that switch role or version
// should remount (key by oppId+resumeId) so `ensure` re-fetches for the new pair.
export function useTailoring(oppId: string, resumeId?: string) {
  const { post } = useCareerApi()
  const [tailoring, setTailoring] = useState<Tailoring | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const ensure = useCallback(async () => {
    if (tailoring || busy) return
    setBusy(true); setErr(null)
    try {
      const body: Record<string, unknown> = { opportunity_id: oppId }
      if (resumeId) body.resume_id = resumeId
      const { tailoring: t } = await post<{ tailoring: Tailoring }>("/api/career/resume/tailor", body)
      setTailoring(t)
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't tailor")
    } finally { setBusy(false) }
  }, [oppId, resumeId, post, tailoring, busy])
  return { tailoring, busy, err, ensure }
}

export function WhyBlock({ item }: { item: FeedItem }) {
  const { evaluation: e } = item
  const liveDrivers = e.fit.drivers.filter((d) => d.hasData)
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>{e.eligibility.reason}</div>
      {liveDrivers.length > 0 && <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text-primary)", marginTop: 2 }}>What went into your fit</div>}
      {liveDrivers.map((d) => (
        <div key={d.key} style={{ display: "grid", gridTemplateColumns: "150px 44px 1fr", gap: 10, alignItems: "center", fontSize: 12.5 }}>
          <span style={{ color: "var(--text-primary)", fontWeight: 600 }}>{d.label}</span>
          <MiniBar score={d.points} />
          <span style={{ color: "var(--text-muted)" }}>{d.reason}</span>
        </div>
      ))}
      <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 2 }}>
        Coverage {Math.round(e.fit.coverage * 100)}% - drivers with no data on your profile are excluded so a blank never reads as a weakness.
      </div>
    </div>
  )
}

// Both the tailored résumé and the cover letter are written in the student's "voice" (their
// profile writing-style block). This nudges them to enrich it so drafts sound more like them.
// Opens the profile Voice tab in a new tab so they don't lose the drawer they're in.
function VoiceNote() {
  return (
    <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
      Want this to sound more like you? <a href="/profile?tab=voice" target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent-text)", fontWeight: 600, textDecoration: "none" }}>Shape your voice in your profile ↗</a>
    </div>
  )
}

// The tailoring result display, keyed only on an opportunity id (and optional résumé
// version). Shared by the drawer's TailorBlock and the résumé screen's "Against a role"
// review, so both render identical role-scoped output. Remount (key by oppId[+resumeId])
// to re-tailor for a new selection.
export function TailorFor({ oppId, resumeId }: { oppId: string; resumeId?: string }) {
  const { tailoring, busy, err, ensure } = useTailoring(oppId, resumeId)
  // Tailoring is an on-demand AI call, so the student fires it EXPLICITLY (matching the cover letter's
  // "Draft a cover letter" button) — not automatically on every drawer open. It used to run in a
  // useEffect on mount, which burned a model call for every role whose tailor section was opened, and
  // again on each re-open (no caching). Most browsed roles are never applied to, so that was wasted cost.
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {err && <div style={{ fontSize: 12.5, color: "var(--status-danger-text)" }}>{err}</div>}
      {!tailoring && !busy ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>See how your résumé matches this role and what to emphasize — grounded in your real résumé, nothing invented.</div>
          <div>
            <button onClick={() => void ensure()} style={{ fontSize: 12.5, fontWeight: 600, padding: "6px 12px", borderRadius: 999, cursor: "pointer", color: "var(--accent-text)", background: "rgba(241,48,9,0.1)", border: "1px solid rgba(241,48,9,0.3)" }}>
              Tailor my résumé for this role
            </button>
          </div>
        </div>
      ) : busy ? (
        <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>Tailoring your résumé for this role…</div>
      ) : !tailoring ? null : !tailoring.available ? (
        <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
          {tailoring.summary} <a href="/max/resume" style={{ color: "var(--accent-text)", fontWeight: 600, textDecoration: "none" }}>Upload a résumé →</a>
        </div>
      ) : (
        <>
          {tailoring.match_score != null && <AtsScore score={tailoring.match_score} matched={tailoring.matched_keywords} missing={tailoring.missing_keywords} />}
          {tailoring.summary && <div style={{ fontSize: 13, color: "var(--text-primary)" }}>{tailoring.summary}</div>}
          {tailoring.emphasize.length > 0 && <TailorList title="Lead with" items={tailoring.emphasize} />}
          {tailoring.rephrase.length > 0 && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)", marginBottom: 4 }}>Reword</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {tailoring.rephrase.map((r, i) => (
                  <div key={i} style={{ fontSize: 12.5 }}>
                    <div style={{ color: "var(--text-muted)", textDecoration: "line-through" }}>{r.from}</div>
                    <div style={{ color: "var(--text-primary)" }}>{r.to}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {tailoring.bullets.length > 0 && <TailorList title="Stronger bullets" items={tailoring.bullets} />}
          {tailoring.address_gaps.length > 0 && <TailorList title="Address these gaps" items={tailoring.address_gaps} muted />}
          <div style={{ fontSize: 11, color: "var(--text-muted)" }}>Grounded only in your real résumé - nothing invented.</div>
          <div>
            <button onClick={() => void ensure()} disabled={busy} style={{ fontSize: 12.5, fontWeight: 600, padding: "6px 12px", borderRadius: 999, cursor: busy ? "default" : "pointer", color: "var(--text-muted)", background: "transparent", border: "1px solid var(--border-subtle)" }}>{busy ? "Re-tailoring…" : "Re-tailor"}</button>
          </div>
          <VoiceNote />
        </>
      )}
    </div>
  )
}

// Opens (or re-opens, never duplicates) the full Document Workspace for this role and
// navigates there. Keeps the existing quick-review sidebar in place (spec: add, don't replace).
function OpenWorkspaceButton({ opportunityId, kind }: { opportunityId: string; kind: "resume" | "cover_letter" }) {
  const { post } = useCareerApi()
  const router = useRouter()
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const open = useCallback(async () => {
    setBusy(true); setErr(null)
    // Open the workspace in a NEW TAB. The document is created first (async), and a window.open
    // AFTER the await is popup-blocked, so open a blank tab now inside the click gesture and
    // redirect it once the id is known. The public careers path is /documents/... (the /max prefix
    // is internal). If the popup is blocked (win === null), fall back to same-tab navigation.
    const win = window.open("", "_blank")
    try {
      const action = kind === "resume" ? "tailor" : "cover_letter"
      const { document } = await post<{ document: { id: string } }>("/api/career/documents", { action, opportunity_id: opportunityId })
      void trackCareer("document_workspace_opened", { kind, from: "drawer", opportunity_id: opportunityId })
      const path = `/documents/${kind === "resume" ? "resume" : "cover-letter"}/${document.id}`
      if (win) { win.opener = null; win.location.href = path }
      else router.push(`/max/documents/${kind === "resume" ? "resume" : "cover-letter"}/${document.id}`)
      setBusy(false)
    } catch (e) {
      if (win) win.close()
      setErr(e instanceof Error ? e.message : "Couldn't open the workspace")
      setBusy(false)
    }
  }, [post, router, kind, opportunityId])
  return (
    <div>
      <Button size="sm" variant="secondary" onClick={() => void open()} disabled={busy}>
        {busy ? "Opening…" : kind === "resume" ? "Open Resume Workspace" : "Open Cover Letter Workspace"}
      </Button>
      {err && <div style={{ fontSize: 12, color: "var(--status-danger-text)", marginTop: 6 }}>{err}</div>}
    </div>
  )
}

export function TailorBlock({ item }: { item: FeedItem }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Review the quick suggestions below, or open the full workspace to edit, save, and download a tailored résumé.</div>
      <OpenWorkspaceButton opportunityId={item.opportunity.id} kind="resume" />
      <TailorFor oppId={item.opportunity.id} />
    </div>
  )
}

// ── Cover letter - grounded draft the student edits, then copies to their application ──
interface CoverLetter { available: boolean; body: string; highlights: string[] }
export function CoverLetterBlock({ item }: { item: FeedItem }) {
  const { post } = useCareerApi()
  const [draft, setDraft] = useState<CoverLetter | null>(null)
  const [text, setText] = useState("")
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const generate = useCallback(async () => {
    setBusy(true); setErr(null)
    try {
      const { cover_letter: cl } = await post<{ cover_letter: CoverLetter }>("/api/career/resume/cover-letter", { opportunity_id: item.opportunity.id })
      setDraft(cl)
      if (cl.available) setText(cl.body)
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't draft a letter")
    } finally { setBusy(false) }
  }, [post, item.opportunity.id])

  const copy = useCallback(async () => {
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1800) } catch { /* clipboard blocked */ }
  }, [text])

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {err && <div style={{ fontSize: 12.5, color: "var(--status-danger-text)" }}>{err}</div>}
      <OpenWorkspaceButton opportunityId={item.opportunity.id} kind="cover_letter" />
      {!draft ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
            Draft a cover letter for this role, grounded in your résumé and coursework. You can edit it before you use it - nothing invented.
          </div>
          <div>
            <button onClick={() => void generate()} disabled={busy} style={{ fontSize: 12.5, fontWeight: 600, padding: "6px 12px", borderRadius: 999, cursor: busy ? "default" : "pointer", color: "var(--accent-text)", background: "rgba(241,48,9,0.1)", border: "1px solid rgba(241,48,9,0.3)" }}>
              {busy ? "Drafting…" : "Draft a cover letter"}
            </button>
          </div>
        </div>
      ) : !draft.available ? (
        <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
          {draft.body} <a href="/max/resume" style={{ color: "var(--accent-text)", fontWeight: 600, textDecoration: "none" }}>Upload a résumé →</a>
        </div>
      ) : (
        <>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={14}
            spellCheck
            style={{ width: "100%", boxSizing: "border-box", fontSize: 13, lineHeight: 1.6, padding: "10px 12px", borderRadius: 10, border: "1px solid var(--border-subtle)", background: "var(--surface)", color: "var(--text-primary)", resize: "vertical", fontFamily: "inherit" }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <button onClick={() => void copy()} style={{ fontSize: 12.5, fontWeight: 600, padding: "6px 12px", borderRadius: 999, cursor: "pointer", color: "var(--accent-text)", background: "rgba(241,48,9,0.1)", border: "1px solid rgba(241,48,9,0.3)" }}>{copied ? "Copied ✓" : "Copy letter"}</button>
            <button onClick={() => void generate()} disabled={busy} style={{ fontSize: 12.5, fontWeight: 600, padding: "6px 12px", borderRadius: 999, cursor: busy ? "default" : "pointer", color: "var(--text-muted)", background: "transparent", border: "1px solid var(--border-subtle)" }}>{busy ? "Regenerating…" : "Regenerate"}</button>
          </div>
          {draft.highlights.length > 0 && (
            <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>Built from: {draft.highlights.slice(0, 5).join(" · ")}</div>
          )}
          <div style={{ fontSize: 11, color: "var(--text-muted)" }}>Edit freely, then copy into your application. Grounded only in your real résumé + coursework.</div>
          <VoiceNote />
        </>
      )}
    </div>
  )
}

export function MiniBar({ score }: { score: number | null }) {
  const pct = score ?? 0
  return (
    <div style={{ height: 6, borderRadius: 999, background: "var(--border-subtle)", overflow: "hidden", width: 44, flexShrink: 0 }}>
      <div style={{ height: "100%", width: `${pct}%`, background: pct >= 70 ? "var(--status-success-text)" : pct >= 40 ? "var(--status-warning-text)" : "var(--text-muted)" }} />
    </div>
  )
}

export function AtsScore({ score, matched, missing }: { score: number; matched: string[]; missing: string[] }) {
  const color = score >= 70 ? "var(--status-success-text)" : score >= 45 ? "var(--status-warning-text)" : "var(--status-danger-text)"
  const chip = (t: string, tone: "success" | "warning") => (
    <span key={t} style={{ fontSize: 11.5, fontWeight: 600, padding: "3px 8px", borderRadius: 999, color: tone === "success" ? "var(--status-success-text)" : "var(--status-warning-text)", background: tone === "success" ? "rgba(61,190,122,0.12)" : "rgba(245,165,95,0.12)", border: `1px solid ${tone === "success" ? "var(--status-success-border)" : "var(--status-warning-border)"}` }}>{t}</span>
  )
  return (
    <div style={{ border: "1px solid var(--border-subtle)", borderRadius: 10, padding: "10px 12px", background: "var(--surface-sunken, transparent)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ fontSize: 22, fontWeight: 800, color, fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>{score}<span style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 600 }}>/100</span></div>
        <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.04em" }}>résumé match for this role</div>
      </div>
      <div style={{ height: 6, borderRadius: 999, background: "var(--border-subtle)", overflow: "hidden", marginTop: 8 }}>
        <div style={{ height: "100%", width: `${score}%`, background: color }} />
      </div>
      {missing.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: "var(--text-muted)", marginBottom: 5 }}>Add these to compete</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>{missing.slice(0, 10).map((k) => chip(k, "warning"))}</div>
        </div>
      )}
      {matched.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: "var(--text-muted)", marginBottom: 5 }}>You already have</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>{matched.slice(0, 10).map((k) => chip(k, "success"))}</div>
        </div>
      )}
    </div>
  )
}

export function TailorList({ title, items, muted }: { title: string; items: string[]; muted?: boolean }) {
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)", marginBottom: 4 }}>{title}</div>
      <ul style={{ margin: 0, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 3 }}>
        {items.map((it, i) => <li key={i} style={{ fontSize: 12.5, color: muted ? "var(--text-muted)" : "var(--text-primary)" }}>{it}</li>)}
      </ul>
    </div>
  )
}

// Wage / start / duration / apply-by - the posting attributes students filter on.
export function PostingMeta({ o }: { o: FeedOpp }) {
  const wage = wageLabel(o)
  const start = fmtDate(o.start_date)
  const dur = durationLabel(o)
  const deadline = fmtDate(o.application_deadline)
  if (!wage && !start && !dur && !deadline) return null
  const item = (icon: string, text: string, strong = false) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, color: strong ? "var(--status-success-text)" : "var(--text-muted)", fontWeight: strong ? 600 : 400 }}>
      <span aria-hidden>{icon}</span>{text}
    </span>
  )
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginTop: 6 }}>
      {wage && item("💵", wage, true)}
      {start && item("🗓", `Starts ${start}`)}
      {dur && item("⏱", dur)}
      {deadline && item("⏳", `Apply by ${deadline}`)}
    </div>
  )
}

// ── OpportunityCard (the ONE role card) ──────────────────────────────────────
// Moved here from /feed so the company page renders the IDENTICAL card (fit Donut, band, why,
// Apply / Why this score / Tailor / Save / Not interested, drawer-open). One source, no drift.
export type DrawerFocus = "why" | "tailor" | "cover_letter" | null

export const FIT_TIP = {
  title: "How Fit is scored",
  body: "How well this role matches your background - a weighted blend of career track, skills, major, experience, coursework, location, projects and target industries. Only drivers we have data for count, so a thin profile scores fewer of them. Confidence shows how much of your profile fed the score.",
  bands: [
    { range: "70-100", label: "Strong match" },
    { range: "55-69", label: "Good match" },
    { range: "40-54", label: "Stretch" },
    { range: "0-39", label: "Reach" },
  ],
} as const

// The band WORD restated in plain language, with what to do about each (so "Reach" isn't misread as
// aspirational-good when it is the LOWEST band). Reuses the same ranges as the Fit tooltip.
export const BAND_TIP = {
  title: "What the match word means",
  body: "A plain read on the Fit score. Strong: you're genuinely competitive. Good: a solid fit. Stretch: a longer shot worth a tailored application. Reach: the lowest band, apply only if it really excites you.",
  bands: FIT_TIP.bands,
} as const

// Confidence is about DATA, not quality. A student seeing a low % next to a high Fit needs to know it
// means "we're missing profile data", not "this match is weak".
export const CONF_TIP = {
  title: "Confidence",
  body: "How much of your profile fed this score, not how good the match is. A low number means we're missing data: add your major, coursework, skills or experience to raise it. It is never a knock on the role.",
} as const

// Grounded mono "Why:" line from the eligibility reason + top fit strengths. No fabrication.
export function whyLine(item: FeedItem): string {
  const e = item.evaluation
  const reason = (e.eligibility.reason || "").replace(/\s+/g, " ").trim()
  const extras = e.fit.strengths.slice(0, 2).join(" · ")
  const body = [reason, extras].filter(Boolean).join(" · ")
  return body ? `Why: ${body}` : ""
}

// Deadline urgency badge: danger inside a week, warning inside two; nothing otherwise. Grounded
// in the real application_deadline; a passed date is left unbadged (eligibility handles closure).
export function deadlineBadge(deadline: string | null): { label: string; tone: "danger" | "warning" } | null {
  if (!deadline) return null
  const d = new Date(`${deadline.slice(0, 10)}T00:00:00`)
  if (isNaN(d.getTime())) return null
  const today = new Date(); today.setHours(0, 0, 0, 0)
  const days = Math.round((d.getTime() - today.getTime()) / 86400000)
  if (days < 0 || days > 14) return null
  const label = days === 0 ? "Closes today" : days === 1 ? "Closes tomorrow" : `Closes in ${days} days`
  return { label, tone: days <= 7 ? "danger" : "warning" }
}

// A deadline group / accent-card counts as "closing" when the posting shuts within this
// many days. Kept as one constant so the card accent, the deadline line, and the feed's
// "Closing this week" group all agree on the window (do not fork it).
export const CLOSING_SOON_DAYS = 10

// Whole days from today until a deadline (0 = today), or null when there is no date, it
// cannot be parsed, or it has already passed (a closed posting isn't "closing").
export function daysUntilDeadline(deadline: string | null): number | null {
  if (!deadline) return null
  const d = new Date(`${deadline.slice(0, 10)}T00:00:00`)
  if (isNaN(d.getTime())) return null
  const today = new Date(); today.setHours(0, 0, 0, 0)
  const days = Math.round((d.getTime() - today.getTime()) / 86400000)
  return days < 0 ? null : days
}

// The plain-language fit band, from the ONE canonical source (web/lib/career/bands.ts), which uses
// the fit engine's own thresholds: Strong >=70, Good 55-69, Stretch 40-54, Reach <40; Not eligible /
// Not scored. Shared by the card, the table, the feed grouping and Today so the bands cannot drift.
export type FitBand = Band
export function fitBand(item: FeedItem): FitBand {
  const e = item.evaluation
  return bandFor(e.fit.score, isEligible(e.eligibility.status))
}

// The two card reasons — one ✓ (a real strength) and one ! (the thing working against it),
// each SPECIFIC to this posting (from the evaluator's fit.strengths / fit.gaps, which are
// role-derived), deduped so the same clause never appears twice and no clause reads
// identically on every card. Falls back to the posting's eligibility reason only when the
// evaluator produced neither. Returns at most two.
//
// RELEASE GATE (isCardReasonTruthful): per-skill matched/missing and coursework clauses are dropped
// here, because the posting data behind them isn't reliable at feed scale (see bands.ts). Only
// track / major / eligibility / location / a soft experience signal survive. Skill-level
// matched/missing stays in the on-demand résumé-tailor panel, which reads the real résumé.
export function cardReasons(item: FeedItem): { positive: boolean; text: string }[] {
  const e = item.evaluation
  const clean = (s: string) => s.replace(/\s+/g, " ").trim()
  const strengths = (e.fit.strengths || []).map(clean).filter(Boolean).filter(isCardReasonTruthful)
  const gaps = (e.fit.gaps || []).map(clean).filter(Boolean).filter(isCardReasonTruthful)
  const seen = new Set<string>()
  const out: { positive: boolean; text: string }[] = []
  const push = (positive: boolean, text: string) => {
    const k = text.toLowerCase()
    if (!k || seen.has(k) || out.length >= 2) return
    seen.add(k); out.push({ positive, text })
  }
  // Prefer one strength + one gap so the card shows both sides; then fill from whichever remains.
  push(true, strengths[0] || "")
  push(false, gaps[0] || "")
  for (const s of strengths) push(true, s)
  for (const g of gaps) push(false, g)
  if (out.length === 0 && e.eligibility.reason) push(true, clean(e.eligibility.reason))
  return out.slice(0, 2)
}

// Colour for the display-size fit number, keyed off the canonical band's tone so the number and the
// band word always agree: success/info get their status colour, everything else stays neutral text.
const fitNumberColor = (band: FitBand): string =>
  band.tone === "success" ? "var(--status-success-text)"
  : band.tone === "info" ? "var(--status-info-text)"
  : band.tone === "danger" ? "var(--text-muted)"
  : "var(--text-primary)"

// The ONE role card, shared by the Matches feed (grouped + table) and the company page.
// Anatomy (top→bottom), REASONS-FIRST: identity + a display-size FIT NUMBER top-right (the number
// Matt asked us to keep visible; its tooltip carries the "how Fit is scored" explanation); the role
// title as the largest text; THE ANSWER — the band WORD + the ✓/! reasons it fits (truthful drivers
// only — per-skill/coursework clauses stay filtered out); then secondary metadata (track / term /
// pay); the deadline + "Why this score" (full drivers); then Apply / Tailor / Save. Kit chrome via
// `.smx-card .smx-card--interactive` (border, radius-lg, elevation, hover lift), `.smx-card--accent`
// when it closes within CLOSING_SOON_DAYS. The single inline override the kit class doesn't cover is
// `background: var(--surface-raised)`, so a card pops off --canvas.
export function OpportunityCard({ item, selected, status, onOpen, onSetStatus, onHide, hideStatusControls }: { item: FeedItem; selected: boolean; status: ApplicationStatus | null; onOpen: (focus: DrawerFocus) => void; onSetStatus: (s: ApplicationStatus | "none") => void; onHide: (scope: HideScope, payload: HidePayload, label: string) => void; hideStatusControls?: boolean }) {
  const { opportunity: o, evaluation: e } = item
  const applyHref = o.apply_url || o.primary_source_url || null
  const band = fitBand(item)
  const reasons = cardReasons(item)
  const days = daysUntilDeadline(o.application_deadline)
  const closingSoon = days != null && days <= CLOSING_SOON_DAYS
  const deadlineStr = fmtDate(o.application_deadline)
  // Season-only badge here: the card footer below already shows the application deadline ("Apply
  // by <date>" / "Closes in N days"), so a rolling role's timing lives there, not in a duplicate chip.
  const term = seasonLabel(o)
  const pay = wageLabel(o)
  const companyNameStyle: CSSProperties = { fontFamily: "var(--font-display, inherit)", fontWeight: 600, fontSize: "var(--type-base, 15px)", color: "var(--text-primary)", textDecoration: "none", width: "fit-content" }
  return (
    <div
      onClick={() => onOpen(null)}
      className={`smx-card smx-card--interactive${closingSoon ? " smx-card--accent" : ""}`}
      style={{
        display: "flex", flexDirection: "column", gap: "var(--space-3)", padding: "var(--space-5)",
        background: "var(--surface-raised)",
        opacity: item.less_like_this ? 0.6 : 1,
        ...(selected ? { borderColor: "var(--accent)" } : null),
      }}
    >
      {/* Identity (left) + a display-size fit number (right). The number is the exact match score the
          engine produced; its tooltip explains how Fit is scored. The band WORD below restates it in
          plain language, and the full driver breakdown is in the drawer's "Why this score". */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "var(--space-3)" }}>
        <div style={{ display: "flex", gap: "var(--space-3)", minWidth: 0 }}>
          <MonogramTile name={o.company_name} logoUrl={o.logo_url} website={o.employer_website} />
          <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
            {o.company_id ? (
              <Link href={`/company/${o.company_id}`} onClick={(ev) => ev.stopPropagation()} title={`${o.company_name || "Company"} - company page`} style={companyNameStyle}>{o.company_name || "-"}</Link>
            ) : (
              <span style={companyNameStyle}>{o.company_name || "-"}</span>
            )}
            <span style={{ fontSize: "var(--type-xs, 12px)", color: "var(--text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{locationLabel(o)}</span>
          </div>
        </div>
        {/* The fit as a display-size number + small-caps label. The Tooltip carries the same
            "how Fit is scored" explanation. */}
        <Tooltip title={FIT_TIP.title} body={FIT_TIP.body} bands={FIT_TIP.bands} side="top" widthPx={280}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", flexShrink: 0, cursor: "help", lineHeight: 1 }}>
            <span style={{ fontFamily: "var(--font-display, inherit)", fontSize: "var(--type-2xl, 26px)", fontWeight: 700, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums", color: fitNumberColor(band) }}>{e.fit.score != null ? e.fit.score : "—"}</span>
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--text-muted)", marginTop: 3 }}>{e.fit.score != null ? "fit" : "not scored"}</span>
          </div>
        </Tooltip>
      </div>

      {/* Role title: the largest text, a focusable link into the detail drawer. */}
      <button
        type="button"
        onClick={(ev) => { ev.stopPropagation(); onOpen(null) }}
        title={o.role_title}
        style={{ background: "none", border: "none", padding: 0, textAlign: "left", cursor: "pointer", font: "inherit", fontFamily: "var(--font-display, inherit)", fontSize: "var(--type-lg, 18px)", fontWeight: 600, color: "var(--text-primary)", lineHeight: 1.3, width: "fit-content" }}
      >
        {o.role_title}
      </button>

      {/* THE ANSWER, leading: the band word (restating the number above in plain language) + the
          reasons it fits. The "how Fit is scored" tooltip lives on the number above. */}
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "var(--space-1)" }}>
          <Tooltip title={BAND_TIP.title} body={BAND_TIP.body} bands={BAND_TIP.bands} side="top" widthPx={260}>
            <span style={{ display: "inline-flex", cursor: "help" }}><Badge tone={band.tone}>{band.label}</Badge></span>
          </Tooltip>
          {item.aligned_to_goal && <GoalChip compact />}
          {item.track_relation === "adjacent" && <AdjacentChip track={o.career_track} />}
          {item.less_like_this && <LessLikeThisChip />}
        </div>
        {reasons.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            {reasons.map((r, i) => (
              <span key={i} style={{ display: "flex", gap: 7, fontSize: "var(--type-sm, 13px)", color: "var(--text-secondary, var(--text-muted))", lineHeight: 1.5 }}>
                <span aria-hidden style={{ flexShrink: 0, fontWeight: 700, color: r.positive ? "var(--status-success-text)" : "var(--status-warning-text)" }}>{r.positive ? "✓" : "!"}</span>
                <span style={{ minWidth: 0 }}>{r.text}</span>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Secondary metadata: track / government / term, then pay. */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-1)", alignItems: "center" }}>
        <Badge tone="neutral">{trackLabel(o.career_track)}</Badge>
        {o.is_government && <Badge tone="info">Government</Badge>}
        {term && <Badge tone={o.term_season && o.term_year && !o.term_inferred ? "neutral" : "warning"}>{term}</Badge>}
        {pay && <span style={{ fontFamily: "var(--font-mono, ui-monospace)", fontSize: "var(--type-2xs, 11px)", color: "var(--status-success-text)", marginLeft: "auto" }}>{pay}</span>}
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "var(--space-2)" }}>
        <span style={{ fontSize: "var(--type-xs, 12px)", fontWeight: closingSoon ? 700 : 500, color: closingSoon ? "var(--status-warning-text)" : "var(--text-muted)" }}>
          {o.application_deadline
            ? (days == null ? "Deadline passed"
              : days === 0 ? "Closes today"
              : days === 1 ? "Closes tomorrow"
              : closingSoon ? `Closes in ${days} days`
              : `Apply by ${deadlineStr}`)
            : "Open deadline"}
        </span>
        <button type="button" onClick={(ev) => { ev.stopPropagation(); onOpen("why") }} style={linkBtn}>Why this score</button>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "var(--space-2)", paddingTop: "var(--space-2)", borderTop: "1px solid var(--border-subtle)" }} onClick={(ev) => ev.stopPropagation()}>
        {applyHref ? (
          <a href={applyHref} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }} onClick={() => void trackCareer("apply_link_clicked", { opportunity_id: o.id, company: o.company_name })}>
            <Button size="sm" variant="primary">Apply</Button>
          </a>
        ) : <Button size="sm" variant="primary" disabled>No link</Button>}
        <Button size="sm" variant="ghost" onClick={() => onOpen("tailor")}>Tailor résumé</Button>
        <Button size="sm" variant="ghost" onClick={() => onOpen("cover_letter")}>Cover letter</Button>
        {/* hideStatusControls: the For You review queue has its own big Save / Not-for-me / Skip buttons,
            and in that context these inline menus were WIRED to no-ops (only "saved" worked; every hide
            scope collapsed to a plain hide), so they advertised actions they wouldn't perform. Hide them
            there and keep them only where they actually work (Explore). */}
        {!hideStatusControls && <StatusMenu status={status} onSet={onSetStatus} />}
        <span style={{ flex: 1 }} />
        <Tooltip title={CONF_TIP.title} body={CONF_TIP.body} side="top" widthPx={240}>
          <span style={{ fontFamily: "var(--font-mono, ui-monospace)", fontSize: 10, color: "var(--text-muted)", cursor: "help" }}>{Math.round(e.fit.confidence * 100)}% confidence</span>
        </Tooltip>
        {/* Labelled (not the bare ⊘ icon) so the Explore card reads the same "Not for me" as the For
            You review queue; the dense FeedTable row keeps the compact icon. The row wraps if tight. */}
        {!hideStatusControls && <NotInterestedMenu opp={o} onHide={onHide} />}
      </div>
    </div>
  )
}
